import React from 'react';
import clsx from 'clsx';
import type { ClientSummary } from '@/features/Clients/Data';
import type { ProcessoMovimentacao, StatusEstadiaCliente } from '../../../models';
import { STATUS_ESTADIA_CLIENTE_LABELS, STATUS_ESTADIA_CLIENTE_OPCOES, PROCESSO_MOVIMENTACAO_LABELS } from '../equipamentosListPage.definitions';
import type { ContagensEstadias, SelecaoEstadias } from '../estadiasCliente.utils';
// Reaproveitados do painel de Solicitações — mesmo visual/comportamento nas duas telas.
import ProjetoFiltroDropdown from '../../SolicitacoesListPage/components/ProjetoFiltroDropdown';
import ResumoFacetaSecao from '../../SolicitacoesListPage/components/ResumoFacetaSecao';

interface EstadiasResumoPainelProps {
  contagens: ContagensEstadias;
  selecao: SelecaoEstadias;
  onChange: (parcial: Partial<SelecaoEstadias>) => void;
  clientes: ClientSummary[];
}

/** Mesma família de cor de STATUS_ESTADIA_CLIENTE_BADGE_CLASS, em tom mais forte pra bolinha. */
const PONTO_STATUS: Record<StatusEstadiaCliente, string> = {
  em_transito: 'bg-blue-500',
  em_homologacao: 'bg-purple-500',
  pronta_aguardando_destino: 'bg-green-500',
  encerrada: 'bg-gray-400',
};

function alternar<T>(lista: T[], valor: T): T[] {
  return lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor];
}

/**
 * Painel de resumo da aba "Estadia em Cliente" — mesmo formato do painel do Controle de
 * Solicitações (SolicitacoesResumoCards), mais enxuto: placar de status clicável, Cliente
 * no cabeçalho (dropdown com busca) e uma linha de Motivo. Tudo liga/desliga e combina.
 */
export default function EstadiasResumoPainel({
  contagens,
  selecao,
  onChange,
  clientes,
}: EstadiasResumoPainelProps) {
  const totalTodos = Array.from(contagens.status.values()).reduce((soma, n) => soma + n, 0);
  const todosSelecionado = selecao.status.length === 0;
  const algumFiltro = Object.values(selecao).some((lista) => lista.length > 0);
  const placar = STATUS_ESTADIA_CLIENTE_OPCOES.map((status) => ({
    status,
    quantidade: contagens.status.get(status) ?? 0,
    selecionado: selecao.status.includes(status),
  }));

  const classeTile = (selecionado: boolean) =>
    clsx('relative flex flex-col items-start gap-0.5 px-4 py-3 text-left transition', selecionado ? 'bg-blue-50' : 'bg-white hover:bg-gray-50');
  const classeNumero = (selecionado: boolean) =>
    clsx('text-2xl font-semibold tabular-nums', selecionado ? 'text-blue-700' : 'text-gray-900');

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
        <h2 className="mr-auto font-montserrat text-sm font-semibold text-gray-900">Resumo</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Cliente</span>
          <ProjetoFiltroDropdown
            clientes={clientes}
            contagem={contagens.clientes}
            selecionados={selecao.clientes}
            onChange={(clientesSelecionados) => onChange({ clientes: clientesSelecionados })}
          />
        </div>
        {algumFiltro && (
          <button
            type="button"
            onClick={() => onChange({ status: [], clientes: [], motivos: [] })}
            className="text-xs font-medium text-blue-600 hover:underline"
          >
            Limpar filtros
          </button>
        )}
      </header>

      <div className="grid grid-cols-2 gap-px border-b border-gray-100 bg-gray-100 sm:grid-cols-5">
        <button type="button" aria-pressed={todosSelecionado} onClick={() => onChange({ status: [] })} className={classeTile(todosSelecionado)}>
          <span className={classeNumero(todosSelecionado)}>{totalTodos}</span>
          <span className="text-xs font-medium text-gray-500">Todos</span>
          {todosSelecionado && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
        </button>
        {placar.map(({ status, quantidade, selecionado }) => (
          <button
            key={status}
            type="button"
            aria-pressed={selecionado}
            onClick={() => onChange({ status: alternar(selecao.status, status) })}
            className={classeTile(selecionado)}
          >
            <span className={classeNumero(selecionado)}>{quantidade}</span>
            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
              <span className={clsx('h-2 w-2 shrink-0 rounded-full', PONTO_STATUS[status])} />
              {STATUS_ESTADIA_CLIENTE_LABELS[status]}
            </span>
            {selecionado && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
          </button>
        ))}
      </div>

      {/* Sem "Na tela: N" (diferente do painel de Solicitações): aqui cada máquina conta uma
          vez só, então o total na tela é sempre a soma dos cards de status marcados. */}
      <div className="px-4 py-3">
        <ResumoFacetaSecao
          titulo="Motivo"
          opcoes={Object.keys(PROCESSO_MOVIMENTACAO_LABELS) as ProcessoMovimentacao[]}
          rotulos={PROCESSO_MOVIMENTACAO_LABELS}
          contagem={contagens.motivos}
          selecionados={selecao.motivos}
          onChange={(motivos) => onChange({ motivos })}
        />
      </div>
    </section>
  );
}
